<script setup lang="ts">
import { computed, defineAsyncComponent, defineComponent, ref, watch } from 'vue'

import type { ChartRootExpose, ChartRootProps } from './types'

import { Icon } from '@/shared/ui/icon'
import Skeleton from '@/shared/ui/skeleton/Skeleton.vue'
import Spinner from '@/shared/ui/spinner/Spinner.vue'

// Lazy public entry for the Highcharts wrapper. This shell stays light (NO `highcharts` import):
// the heavy implementation is pulled via defineAsyncComponent, so `highcharts` splits into its own
// Vite chunk and never enters the main bundle (contract 02 §4, §16). Kills the legacy
// `app.component('highcharts', …)` global registration (main.js:181).
//
// Contract 02 §14: a lazy-chunk load failure must render an inline error state WITH retry — never
// a blank pane. defineAsyncComponent's errorComponent receives none of the original props, so the
// fallback is owned HERE where `options` stays bound and a fresh `:key` re-runs the import.
const props = withDefaults(defineProps<ChartRootProps>(), {
  state: 'ready',
  loadingShape: 'bar',
  constructorType: 'chart',
  immutable: false,
  updateArgs: () => [true, true] as const,
  menuItems: () => [],
  loadErrorMessage: 'ui.chart.loadError',
  retryLabel: 'ui.chart.retry',
  refreshing: false,
})

const emit = defineEmits<{ retry: [] }>()

// The loading silhouettes belong to the primitive (contract 29 §3.3). They reproduce v3's
// `Skeletons/{Bar,Line,Pie,Bubble}ChartSkeleton.vue` one-to-one — geometry, spacing and the
// per-element gray step each original painted with (the `Skeleton` ramp tones). Radii are
// spelled as v3's Tailwind defaults because this design system remaps `rounded-md`/`rounded-lg`.
// Skeleton owns the shimmer and its reduced-motion behavior.
const V3_RADIUS_SM = '0.25rem'
const V3_RADIUS_MD = '0.375rem'
const V3_RADIUS_TOP_MD = '0.375rem 0.375rem 0 0'
const LOADING_BAR_PAIRS: readonly (readonly [actual: string, previous: string])[] = [
  ['25%', '15%'],
  ['20%', '30%'],
  ['80%', '95%'],
  ['10%', '5%'],
  ['28%', '26%'],
  ['12%', '18%'],
  ['22%', '18%'],
  ['25%', '30%'],
  ['10%', '20%'],
  ['5%', '10%'],
]
const LOADING_LINE_Y_TICKS = ['2.5rem', '3rem', '2.5rem', '2rem'] as const
const LOADING_SERIES_PATHS = [
  { d: 'M0 40 C150 50, 350 30, 500 45 S800 60, 900 50', tone: 'text-skeleton-bold' },
  { d: 'M0 60 C150 70, 350 50, 500 65 S800 80, 900 70', tone: 'text-skeleton-bold/80' },
  { d: 'M0 120 C150 110, 350 130, 500 115 S800 100, 900 125', tone: 'text-skeleton-bold/60' },
  { d: 'M0 220 C150 210, 350 230, 500 215 S800 200, 900 225', tone: 'text-skeleton-medium' },
  { d: 'M0 280 C150 290, 350 270, 500 285 S800 300, 900 290', tone: 'text-skeleton-medium/80' },
  { d: 'M0 320 C150 310, 350 330, 500 315 S800 300, 900 325', tone: 'text-skeleton-medium/60' },
] as const
const LOADING_BUBBLES = [
  { size: '5rem', bottom: '25%', left: '5%' },
  { size: '2rem', bottom: '10%', left: '15%' },
  { size: '4rem', bottom: '30%', left: '22%' },
  { size: '5rem', bottom: '25%', left: '35%' },
  { size: '3.5rem', bottom: '15%', left: '40%' },
  { size: '4.5rem', bottom: '50%', left: '50%' },
  { size: '6rem', bottom: '40%', left: '60%' },
  { size: '3rem', bottom: '35%', left: '72%' },
  { size: '5.5rem', bottom: '75%', left: '80%' },
  { size: '4.5rem', bottom: '70%', left: '88%' },
] as const

const loadFailed = ref(false)
const runtimeFailed = ref(false)
const attempt = ref(0)
const inner = ref<ChartRootExpose>()

const resolvedState = computed(() => {
  if (props.state !== 'ready') return props.state
  return props.options ? 'ready' : 'empty'
})

const showError = computed(
  () => resolvedState.value === 'error' || loadFailed.value || runtimeFailed.value,
)

// Rendered only if the chunk errors before `loadFailed` swaps the template to the error state.
const EmptyFailure = defineComponent({ name: 'ChartRootLoadError', render: () => null })

const HighchartsChart = defineAsyncComponent({
  loader: () => import('./HighchartsChart.vue'),
  errorComponent: EmptyFailure,
  onError(_error, _retry, fail) {
    loadFailed.value = true
    fail()
  },
})

function retry(): void {
  loadFailed.value = false
  runtimeFailed.value = false
  // Bumping the key remounts the async component, which re-invokes the (previously rejected)
  // dynamic import — a genuine retry, not a cached failure.
  attempt.value += 1
}

function retryError(): void {
  if (resolvedState.value === 'error') {
    emit('retry')
    return
  }
  retry()
}

function onConstructorError(): void {
  runtimeFailed.value = true
}

watch(
  () => props.constructorType,
  () => {
    if (runtimeFailed.value) retry()
  },
)

defineExpose<ChartRootExpose>({
  get chart() {
    return inner.value?.chart ?? null
  },
})
</script>

<template>
  <div
    v-if="resolvedState === 'loading'"
    class="ui-chart__loading w-full px-4 py-6"
    aria-busy="true"
  >
    <slot name="loading">
      <div v-if="loadingShape === 'line'" class="w-full">
        <div class="ui-chart__loading-legend mb-6 flex flex-wrap justify-center gap-x-6 gap-y-2">
          <span v-for="chip in 6" :key="chip" class="flex items-center gap-2">
            <Skeleton width="2rem" height="0.25rem" radius="var(--ui-radius-full)" tone="medium" />
            <Skeleton width="7rem" height="1rem" :radius="V3_RADIUS_MD" tone="soft" />
          </span>
        </div>
        <div class="ui-chart__loading-frame flex gap-4">
          <div
            class="ui-chart__loading-axis-y flex h-96 w-16 flex-col items-end justify-between py-2"
          >
            <Skeleton
              v-for="(tick, index) in LOADING_LINE_Y_TICKS"
              :key="index"
              :width="tick"
              height="1rem"
              :radius="V3_RADIUS_MD"
              tone="soft"
            />
          </div>
          <div class="w-full flex-1">
            <div class="ui-chart__loading-plot relative h-96 w-full">
              <span
                v-for="line in 4"
                :key="line"
                class="absolute inset-x-0 block"
                :style="{ top: `${(line - 1) * 33.3}%` }"
                aria-hidden="true"
              >
                <Skeleton height="1px" radius="0" tone="soft" />
              </span>
              <svg
                class="ui-chart__loading-series h-full w-full"
                viewBox="0 0 900 350"
                preserveAspectRatio="none"
                fill="none"
                aria-hidden="true"
              >
                <path
                  v-for="(path, index) in LOADING_SERIES_PATHS"
                  :key="index"
                  :d="path.d"
                  :class="path.tone"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="ui-chart__loading-axis-x mt-2 flex w-full justify-between pt-4">
              <Skeleton
                v-for="tick in 7"
                :key="tick"
                width="3rem"
                height="1rem"
                :radius="V3_RADIUS_MD"
                tone="soft"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="loadingShape === 'pie'" class="mx-auto w-full p-4">
        <div class="ui-chart__loading-pie relative mx-auto mb-8 h-64 w-64">
          <Skeleton width="100%" height="100%" radius="var(--ui-radius-full)" tone="soft" />
          <span
            class="ui-chart__loading-pie-hole absolute top-1/2 left-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-surface"
            aria-hidden="true"
          />
        </div>
        <div
          class="ui-chart__loading-legend flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
        >
          <span v-for="chip in 6" :key="chip" class="flex items-center gap-2">
            <Skeleton width="1rem" height="1rem" :radius="V3_RADIUS_SM" tone="soft" />
            <Skeleton width="4rem" height="1rem" :radius="V3_RADIUS_MD" tone="medium" />
          </span>
        </div>
      </div>
      <div
        v-else-if="loadingShape === 'bar'"
        class="mx-auto w-full max-w-6xl rounded-md bg-surface p-4 sm:p-6"
      >
        <div class="relative mb-6 flex items-center justify-center">
          <div class="flex flex-col items-center gap-3">
            <Skeleton width="8rem" height="1.5rem" :radius="V3_RADIUS_MD" tone="medium" />
            <div class="ui-chart__loading-legend flex items-center gap-4">
              <span v-for="chip in 2" :key="chip" class="flex items-center gap-2">
                <Skeleton
                  width="1rem"
                  height="1rem"
                  radius="var(--ui-radius-full)"
                  :tone="chip === 1 ? 'bold' : 'medium'"
                />
                <Skeleton width="4rem" height="1rem" :radius="V3_RADIUS_MD" tone="soft" />
              </span>
            </div>
          </div>
        </div>
        <div class="ui-chart__loading-bars flex h-80 w-full items-end gap-4 pt-10 sm:gap-6">
          <span
            v-for="(pair, index) in LOADING_BAR_PAIRS"
            :key="index"
            class="flex h-full flex-1 flex-col items-center gap-3"
          >
            <span class="flex h-full w-full items-end justify-center gap-1.5">
              <Skeleton width="100%" :height="pair[0]" :radius="V3_RADIUS_TOP_MD" tone="bold" />
              <Skeleton width="100%" :height="pair[1]" :radius="V3_RADIUS_TOP_MD" tone="medium" />
            </span>
            <span class="flex w-full flex-col items-center gap-2">
              <Skeleton width="80%" height="1.25rem" :radius="V3_RADIUS_MD" tone="medium" />
              <Skeleton height="0.75rem" :radius="V3_RADIUS_MD" tone="soft" />
            </span>
          </span>
        </div>
      </div>
      <div v-else class="py-16">
        <div class="mx-auto w-full max-w-5xl p-4">
          <div class="ui-chart__loading-frame flex gap-4">
            <div
              class="ui-chart__loading-axis-y flex h-80 w-12 flex-col-reverse items-end justify-between py-2"
            >
              <Skeleton
                v-for="tick in 6"
                :key="tick"
                height="1rem"
                :radius="V3_RADIUS_MD"
                tone="soft"
              />
            </div>
            <div class="ui-chart__loading-plot relative h-80 w-full flex-1 overflow-hidden">
              <span
                v-for="line in 9"
                :key="`v-${line}`"
                class="absolute inset-y-0 w-px bg-skeleton-soft/80"
                :style="{ left: `${(line - 1) * 11.1}%` }"
                aria-hidden="true"
              />
              <span
                v-for="line in 6"
                :key="`h-${line}`"
                class="absolute inset-x-0 h-px bg-skeleton-soft/80"
                :style="{ bottom: `${(line - 1) * 20}%` }"
                aria-hidden="true"
              />
              <span
                v-for="(bubble, index) in LOADING_BUBBLES"
                :key="index"
                class="ui-chart__loading-bubble absolute block"
                :style="{
                  width: bubble.size,
                  height: bubble.size,
                  bottom: bubble.bottom,
                  left: bubble.left,
                }"
              >
                <Skeleton
                  class="opacity-80"
                  width="100%"
                  height="100%"
                  radius="var(--ui-radius-full)"
                  tone="medium"
                />
              </span>
            </div>
          </div>
          <div class="ui-chart__loading-axis-x mt-2 flex w-full justify-between pl-16">
            <Skeleton
              v-for="tick in 9"
              :key="tick"
              width="2rem"
              height="1rem"
              :radius="V3_RADIUS_MD"
              tone="soft"
            />
          </div>
        </div>
      </div>
    </slot>
  </div>
  <div
    v-else-if="resolvedState === 'empty'"
    class="ui-chart__empty grid h-max lg:h-125 w-full place-items-center"
  >
    <slot name="empty" />
  </div>
  <div
    v-else-if="showError"
    class="ui-chart__error flex h-max lg:h-125 w-full flex-col items-center justify-center gap-sm rounded-lg border border-border bg-surface p-md text-center text-muted"
    role="alert"
  >
    <slot name="error" :retry="retryError">
      <Icon class="ui-chart__error-icon text-danger" name="alert-triangle" :size="32" />
      <p class="ui-chart__error-text m-0 text-sm">{{ loadErrorMessage }}</p>
      <button
        type="button"
        class="ui-chart__retry inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-border bg-surface text-fg transition-colors duration-150 hover:bg-surface-sunken"
        :aria-label="retryLabel"
        @click="retryError"
      >
        <Icon name="arrow-rotate-right" :size="18" />
      </button>
    </slot>
  </div>
  <div v-else class="ui-chart__ready relative h-full w-full">
    <HighchartsChart
      :key="attempt"
      ref="inner"
      :options="options"
      :constructor-type="constructorType"
      :immutable="immutable"
      :update-args="updateArgs"
      :menu-items="menuItems ?? []"
      @constructor-error="onConstructorError"
    />
    <div
      v-if="refreshing"
      class="ui-chart__refresh-overlay absolute inset-0 z-10 flex items-center justify-center rounded-md bg-[color:var(--ui-overlay-content-backdrop)]"
      role="status"
      aria-live="polite"
    >
      <Spinner size="lg" />
    </div>
  </div>
</template>
